import React, { useEffect } from "react";
import { Form, Input } from "antd";
import TagsInput from "../components/TagsInput";
import MultipleChoice from "../components/MultipleChoice";
import StateCapabilityEditor from "../components/StateCapabilityEditor";

import { CONSTANTS_WEBSITE } from "../constants";
import { normalizeStateCapabilities } from "../stateCapabilities";
import StepHeader from "./StepHeader";

import type { TypePersonalInfoProps } from "./PersonalInfo";
import type { ValidatorRule } from "antd/node_modules/rc-field-form/lib/interface";

export interface TypeVerifyCredentialsProps extends TypePersonalInfoProps {}

type TypeVerifyCredentialsComp = React.FC<TypeVerifyCredentialsProps> & {
  REQUIRED: typeof FORM_REQUIRED_FIELDS;
};

// 必填的字段
const FORM_REQUIRED_FIELDS = {
  NMLS: ["NMLS"],
  States: ["LandingPageSetUp", "States"],
  StateCapabilities: ["LandingPageSetUp", "StateCapabilities"],
};

const SUGGESTED_TAGS = [
  "I speak Mandarin",
  "Self-Employed borrowers",
  "Bay Area for 10+ years",
];

/**
 * @name VerifyCredentials 验证凭证
 * @description Verify your Credentials
 */
const VerifyCredentials: TypeVerifyCredentialsComp = ({
  auditTips,
  badge,
  intro,
  title = "Verify your credentials",
}) => {
  const form = Form.useFormInstance();
  const legacyStates = Form.useWatch(FORM_REQUIRED_FIELDS.States, form);
  const stateCapabilities = Form.useWatch(
    FORM_REQUIRED_FIELDS.StateCapabilities,
    form,
  );

  useEffect(() => {
    if (Array.isArray(stateCapabilities) && stateCapabilities.length) return;
    if (!Array.isArray(legacyStates) || !legacyStates.length) return;

    form.setFieldValue(
      FORM_REQUIRED_FIELDS.StateCapabilities,
      normalizeStateCapabilities(undefined, legacyStates),
    );
  }, [form, legacyStates, stateCapabilities]);

  const checkNMLSID: ValidatorRule["validator"] = (_rule, value) => {
    if (Number.isFinite(Number(value))) {
      if (!/^[0-9]{1,7}$/.test(value)) {
        return Promise.reject("NMLS ID must be 1-7 digits");
      }
      if (/^(\d)\1+$/.test(value)) {
        return Promise.reject("Please enter the correct NMLS ID");
      }
    } else {
      return Promise.reject("Please enter the correct NMLS ID");
    }
    return Promise.resolve();
  };

  return (
    <div className="layout-profile-ctx">
      <StepHeader title={title} badge={badge} intro={intro} />

      <Form.Item
        label="NMLS ID"
        tooltip={auditTips}
        name={FORM_REQUIRED_FIELDS.NMLS}
        rules={[
          { validator: checkNMLSID },
          { required: true, message: "Please enter your NMLS ID" },
        ]}
      >
        <Input placeholder="NMLS ID must be numeric" />
      </Form.Item>

      <Form.Item
        tooltip={auditTips}
        label="License state"
        name={FORM_REQUIRED_FIELDS.StateCapabilities}
        rules={[
          {
            type: "array",
            required: true,
            message: "Please select License state",
          },
          {
            validator: (_rule, value) => {
              if (!Array.isArray(value) || !value.length) {
                return Promise.resolve();
              }
              if (value.some((item) => !item?.qm && !item?.nonQm)) {
                return Promise.reject(
                  "Please select QM, Non-QM, or both for every state",
                );
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <StateCapabilityEditor
          onStatesChange={(states) =>
            form.setFieldValue(FORM_REQUIRED_FIELDS.States, states)
          }
        />
      </Form.Item>

      <Form.Item
        label="Loan Types"
        initialValue={["Conventional"]}
        name={["LandingPageSetUp", "LoanTypes"]}
        rules={[
          {
            type: "array",
            required: true,
            message: "Please select Loan types",
          },
        ]}
      >
        <MultipleChoice options={CONSTANTS_WEBSITE.LOAN_TYPES} />
      </Form.Item>

      <Form.Item
        label="Specialties tag"
        name={["LandingPageSetUp", "TagsList"]}
        tooltip="Highlight your specialties and strengths with up to 5 tags"
      >
        <TagsInput max={5} suggestedTags={SUGGESTED_TAGS} />
      </Form.Item>

      <Form.Item
        name={["LandingPageSetUp", "appLink"]}
        label="Application button URL (optional)"
        rules={[{ type: "url", message: "Please enter a valid URL" }]}
        tooltip={{
          title:
            'This field allows you to customize the URL for the "Apply now" buttons on your website.\n\nBy default, the buttons will link to your system\'s application URL. If you prefer to direct your website visitors to a different application URL, please enter your desired URL here.\n\nMake sure the URL begins with "http://" or "https://".',
          overlayStyle: { whiteSpace: "pre-line" },
        }}
      >
        <Input allowClear placeholder="Please enter the url (https://)" />
      </Form.Item>
    </div>
  );
};

VerifyCredentials.REQUIRED = FORM_REQUIRED_FIELDS;

export default VerifyCredentials;
